import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Home, ArrowRight, AlertCircle, Building2 } from "lucide-react";

export default function Services() {
  const [, navigate] = useLocation();

  const services = [
    {
      id: 1,
      title: "Book Appointments",
      description: "Schedule meetings with Executive Committee members to discuss community matters and concerns",
      icon: Calendar,
      color: "from-blue-500 to-blue-600",
      textColor: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-950",
      href: "/appointments",
      cta: "Schedule Now",
    },
    {
      id: 2,
      title: "Check Amenities Availability",
      description: "View the 90-day calendar to see which amenities are available for booking without revealing who booked them",
      icon: Clock,
      color: "from-purple-500 to-purple-600",
      textColor: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-950",
      href: "/amenities-availability",
      cta: "View Calendar",
    },
    {
      id: 3,
      title: "Book Amenities",
      description: "Reserve community amenities like clubhouse, gym, swimming pool, and sports facilities for your events",
      icon: Home,
      color: "from-green-500 to-green-600",
      textColor: "text-green-600",
      bgColor: "bg-green-50 dark:bg-green-950",
      href: "/amenities-booking",
      cta: "Book Now",
    },
    {
      id: 4,
      title: "Apartment Marketplace",
      description: "Buy, sell, or rent apartments within the Purva Swanlake community. Connect directly with residents and find your next home",
      icon: Building2,
      color: "from-orange-500 to-orange-600",
      textColor: "text-orange-600",
      bgColor: "bg-orange-50 dark:bg-orange-950",
      href: "/apartments",
      cta: "Browse Listings",
    },
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-6xl mx-auto">
        {/* Work in Progress Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-full px-4 py-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Work in Progress</span>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Services</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Manage your community life with ease. Book appointments with EC members, check amenity availability, and reserve facilities for your needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <Card
                key={service.id}
                className="group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden cursor-pointer"
                onClick={() => navigate(service.href)}
              >
                {/* Gradient Background */}
                <div className={`h-1 bg-gradient-to-r ${service.color}`}></div>

                <CardHeader className={`${service.bgColor} border-b`}>
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-lg ${service.bgColor} group-hover:scale-110 transition-transform`}>
                      <IconComponent className={`w-6 h-6 ${service.textColor}`} />
                    </div>
                  </div>
                  <CardTitle className="mt-4 text-xl">{service.title}</CardTitle>
                </CardHeader>

                <CardContent className="pt-6">
                  <CardDescription className="text-base mb-6 leading-relaxed">
                    {service.description}
                  </CardDescription>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(service.href);
                    }}
                    className="w-full group/btn"
                  >
                    {service.cta}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-semibold">💡 Tip:</span> All services are available 24/7. EC members will respond to appointment requests within 24 hours. For urgent matters, please contact us directly at{" "}
            <a href="tel:8838907864" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">
              88389 07864
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
